/* eslint-disable no-param-reassign */

import { AttributesToRemoveInfo } from 'src/containers/ProjectDiagram/hooks/remove/types';
import {
  ActiveDiagram,
  CommitTransform,
  Diagram,
  DiagramLink,
  DiagramLinkLabel,
  DiagramNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { checkAndConfirmCommit } from 'src/containers/ProjectDiagram/utils/checkAndConfirmCommit';

export enum DiagramModelCommitLayout {
  SKIP = 'SKIP',
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
}

export type DiagramModelCommitOptions = {
  attributesToRemoveInfo?: AttributesToRemoveInfo[];
  layout?: DiagramModelCommitLayout;
  layoutPartial?: Set<go.Key>;
  shouldCommit?: boolean;
  skipsCheck?: boolean;
  tname?: string;
};

// The transform's runtime contract is diagram-optional; the headless path never
// supplies one and only ever runs model-only transforms, so the cast is safe.
const NO_DIAGRAM = undefined as unknown as Diagram;

/**
 * Headless counterpart of the diagram commit path. Runs a mutation transform
 * against a bare `GraphLinksModel`, honoring `shouldCommit` / `skipsCheck` /
 * `tname`, but skips every layout, route, and visual concern that needs a
 * `go.Diagram`. The read-only gate lives in {@link commitDiagramModel}, which is
 * the only caller.
 */
const commitHeadlessModel = async (
  model: Model,
  commit: CommitTransform,
  options: DiagramModelCommitOptions
) => {
  const {
    attributesToRemoveInfo,
    shouldCommit = true,
    skipsCheck = false,
    tname,
  } = options;

  if (!shouldCommit) {
    commit(model, NO_DIAGRAM);
    return true;
  }

  if (skipsCheck) {
    model.startTransaction(tname);
    commit(model, NO_DIAGRAM);
    model.commitTransaction(tname);
    return true;
  }

  return checkAndConfirmCommit(model, commit, tname, attributesToRemoveInfo);
};

/**
 * Commits a model mutation against whatever is backing edits. For the headless
 * facade it runs the model-only path; for a real diagram it also handles layout,
 * routing, and the undo manager. Both arms share the read-only gate, so edit
 * hooks commit the same transform regardless of whether the canvas is built.
 */
export const commitDiagramModel = async (
  active: ActiveDiagram,
  commit: CommitTransform,
  options: DiagramModelCommitOptions = {}
) => {
  const {
    tname = undefined,
    layout = DiagramModelCommitLayout.SKIP,
    layoutPartial = new Set(),
    shouldCommit = true,
    skipsCheck = false,
  } = options;

  // Read-only collaborators must not mutate the model programmatically (GoJS
  // only blocks gestures) — blocked edits would silently diverge from the
  // shared doc since the middleware write gate drops the resulting dispatch.
  // System commits (skipsCheck — initial-layout normalization) still run.
  if (active.isReadOnly && !skipsCheck) {
    return false;
  }

  if (active.headless) {
    return commitHeadlessModel(active.model, commit, options);
  }

  const diagram = active;
  const prevIsOngoing = diagram.layout.isOngoing;

  if (layout === DiagramModelCommitLayout.SKIP) {
    diagram.layout.isOngoing = false;
  } else if (layout === DiagramModelCommitLayout.PARTIAL) {
    diagram.nodes.each((n) => {
      // data can be null for temporary parts
      const node = n as DiagramNode | DiagramLinkLabel | { data: null };
      if (node.data) {
        node.forceNewPosition = layoutPartial.has(node.data.key);
      }
    });
    diagram.links.each((l) => {
      // data can be null for temporary parts
      const link = l as DiagramLink | { data: null };
      if (link.data) {
        link.forceNewPosition = layoutPartial.has(link.data.key);
      }
    });
  }

  let didCommit = true;
  if (!shouldCommit) {
    commit(diagram.model, diagram);
  } else if (skipsCheck) {
    // anything that skips check should not be recorded by undomanager,
    // but we still want layout to happen
    diagram.commit((diagram) => commit(diagram.model, diagram), null);
  } else {
    didCommit = await checkAndConfirmCommit(
      diagram.model,
      commit,
      tname,
      options.attributesToRemoveInfo,
      diagram
    );
  }

  if (
    layout === DiagramModelCommitLayout.FULL ||
    layout === DiagramModelCommitLayout.PARTIAL
  ) {
    diagram.nodes.each((node) => {
      (node as DiagramNode).forceNewPosition = false;
    });
    diagram.links.each((l) => {
      const link = l as DiagramLink;
      link.forceNewPosition = false;
      if (link.isNewlyAdded) {
        // Ensure that go.js always applies the layout to newly-created links
        link.invalidateRoute();
        link.isNewlyAdded = false;
      }
    });
  } else if (layout === DiagramModelCommitLayout.SKIP) {
    diagram.layout.isOngoing = prevIsOngoing;
  }

  return didCommit;
};

/**
 * Makes a commit while having the transaction not recorded by the UndoManager.
 * Use this when layout behavior is not needed (ex: highlighting, deletions)
 */
export const commitDiagramWhileSkippingUndoManager = (
  diagram: Diagram,
  commit: CommitTransform,
  tname?: string
) => {
  diagram.skipsUndoManager = true;
  diagram.commit((d) => commit(d.model, d), tname || null);
  diagram.skipsUndoManager = false;
};
